import React, { createContext, useCallback, useContext, useMemo } from "react";
import {
	AppRoutes,
	DashboardModules,
	KioskSubModules,
} from "../../config/app.config";
import { ROLE_MACHINE_HOME_ROUTE, ROLE_PERMISSIONS } from "../../config/role.config";
import {
	pathnameMatchesAllowedPatterns,
	deriveAccess,
} from "./rbacUtils";
import { isEmpty } from "../../util/util";

const RBACContext = createContext(null);

const defaultKitchenHome = AppRoutes[DashboardModules.KIOSK][KioskSubModules.MACHINE].KITCHEN_MODULES;

export function RBACProvider({ children, role, loading }) {
	const permissions = useMemo(
		() => deriveAccess(ROLE_PERMISSIONS, role),
		[role]
	);

	const effectiveLoading = useMemo(() => {
		if(loading || isEmpty(permissions)) return true;
		return false;
	}, [permissions]);

	const { allowedModules, allowedPages, allowedSidebarLinksPerModule } = permissions;

	const canAccessModule = useCallback(
		(module) => allowedModules.includes(module),
		[allowedModules]
	);

	const canAccessPage = useCallback(
		(pathname) => pathnameMatchesAllowedPatterns(pathname, allowedPages),
		[allowedPages]
	);

	const resolveMachineHomeUrl = useCallback(
		(machineId) => {
			const homeDef = ROLE_MACHINE_HOME_ROUTE[role] ?? defaultKitchenHome;
			const preferred = homeDef.getPath(machineId);
			return preferred;
		},
		[role]
	);

	const value = useMemo(
		() => ({
			loading: effectiveLoading,
			role,
			canAccessModule,
			canAccessPage,
			resolveMachineHomeUrl,
			allowedSidebarLinksPerModule,
		}),
		[
			effectiveLoading,
			role,
			canAccessModule,
			canAccessPage,
			resolveMachineHomeUrl,
			allowedSidebarLinksPerModule,
		]
	);

	return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
}

export function useRBAC() {
	const ctx = useContext(RBACContext);
	if (!ctx) {
		throw new Error("useRBAC must be used within RBACProvider");
	}
	return ctx;
}
